import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { useNotify } from '@/components/snackbar';
import useTableFilter from '../hooks/useTableFilter';
import {
  acceptAffiliateService,
  blockAffiliateService,
  clearAffiliatesCache,
  createAffiliateService,
  deleteAffiliateService,
  getAffiliates,
  rejectAffiliateService,
  unblockAffiliateService,
  updateAffiliateService,
} from '../services/affiliates';
import { getProductsService } from '../services/products';
import { FormScopes } from '../utils/form';

export const AffiliateStatus = {
  active: {
    label: 'Ativo',
    value: 'active',
    color: 'success',
  },
  waiting: {
    label: 'Analisando',
    value: 'waiting',
    color: 'warning',
  },
  refused: {
    label: 'Rejeitado',
    value: 'refused',
    color: 'error',
  },
  canceled: {
    label: 'Cancelado',
    value: 'canceled',
    color: 'error',
  },
  blocked: {
    label: 'Bloqueado',
    value: 'blocked',
    color: 'error',
  },
};

export const TabStatusMap = {
  active: ['active'],
  pending: ['waiting'],
  disabled: ['refused', 'blocked', 'canceled'],
};

export const resolveAffiliatesStatus = (tabStatus, filterStatus) => {
  if (Array.isArray(filterStatus) && filterStatus.length > 0) {
    return filterStatus;
  }

  return tabStatus;
};

export const AffiliatesContext = createContext({
  table: {},
  affiliates: [],
  fetching: false,
  refreshAffiliates: async () => {},
  affiliatesUpdatedAt: 0,
  count: 0,
  setTotal: () => {},
  accepting: false,
  acceptAffiliate: () => {},
  blockAffiliate: () => {},
  blocking: false,
  unblockAffiliate: () => {},
  unblocking: false,
  deleting: false,
  deleteAffiliate: () => {},
  creating: false,
  createAffiliate: () => {},
  updateAffiliate: () => {},
  rejecting: false,
  rejectAffiliate: () => {},
  products: [],
  fetchingProducts: false,
  ensureProducts: () => {},
  scope: FormScopes.INDEX,
  setScope: () => {},
  form: {},
  update: () => {},
  loadingUpdate: false,
});

export const AffiliatesDefaultFilter = {
  products: [],
  status: [],
};

const AffiliatesProvider = ({ children, type }) => {
  const [searchParams] = useSearchParams();

  const [tabValue, setTabValue] = useState(searchParams.get('tab') || 'active');

  const [scope, setScope] = useState(FormScopes.INDEX);

  const queryClient = useQueryClient();

  const notify = useNotify();

  const table = useTableFilter({
    defaultFilter: AffiliatesDefaultFilter,
  });

  const tabStatus = useMemo(() => TabStatusMap[tabValue] || [], [tabValue]);
  const status = useMemo(
    () => resolveAffiliatesStatus(tabStatus, table.filter.status),
    [tabStatus, table.filter.status]
  );

  const schema = yup.object().shape({
    commission: yup
      .number()
      .min(1, 'A comissão tem que ser igual ou maior que 1%')
      .max(95, 'A comissão não pode ser maior que 95%')
      .required(),
  });

  const form = useForm({
    resolver: yupResolver(schema),
  });

  const {
    data: { products },
    isFetching: fetchingProducts,
    refetch: refetchProducts,
  } = useQuery({
    queryFn: () =>
      getProductsService({
        page: 1,
        limit: 1000,
      }),
    queryKey: ['products'],
    initialData: {
      products: [],
    },
    enabled: false,
    staleTime: 600000,
  });

  const ensureProducts = useCallback(() => {
    const state = queryClient.getQueryState(['products']);
    const lastUpdatedAt = state?.dataUpdatedAt || 0;
    const isFresh = Date.now() - lastUpdatedAt < 600000;
    const productsData = state?.data?.products;
    const hasProducts = Array.isArray(productsData) && productsData.length > 0;

    if (
      !state ||
      state.status === 'idle' ||
      state.status === 'error' ||
      !isFresh ||
      !hasProducts
    ) {
      return refetchProducts();
    }

    return Promise.resolve(state.data);
  }, [queryClient, refetchProducts]);

  const affiliatesQueryKey = ['affiliates', table.page, table.search, status, table.filter];

  const {
    data: { results: affiliates, count },
    isFetching,
    refetch: refetchAffiliates,
    dataUpdatedAt: affiliatesUpdatedAt,
  } = useQuery(affiliatesQueryKey, {
    queryFn: () =>
      getAffiliates({
        page: table.page,
        search: table.search,
        type: 'producer',
        status,
        filter: table.filter,
      }),
    initialData: {
      affiliates: [],
      count: 0,
    },
  });

  const refreshAffiliates = useCallback(() => {
    clearAffiliatesCache();
    return refetchAffiliates();
  }, [refetchAffiliates]);

  useEffect(() => {
    table.setSelected([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const { mutateAsync: createAffiliate, isLoading: creating } = useMutation({
    mutationFn: createAffiliateService,
    onSuccess: () => {
      queryClient.invalidateQueries('products');

      notify.success('Afiliação criada com sucesso!');

      setScope(FormScopes.INDEX);
    },
    onError: (error) => {
      notify.error(error);
    },
  });

  const { mutateAsync: unblockAffiliate, isLoading: unblocking } = useMutation({
    mutationFn: (ids) => unblockAffiliateService(ids),
    onSuccess: () => {
      queryClient.invalidateQueries('products');

      notify.success('Afiliação desbloqueada com sucesso!');

      setScope(FormScopes.INDEX);

      table.setSelected([]);
    },
    onError: (error) => {
      notify.error(error);
    },
  });

  const { mutateAsync: blockAffiliate, isLoading: blocking } = useMutation({
    mutationFn: (ids) => blockAffiliateService(ids),
    onSuccess: () => {
      queryClient.invalidateQueries('products');

      notify.success('Afiliação bloqueada com sucesso!');

      setScope(FormScopes.INDEX);

      table.setSelected([]);
    },
    onError: (error) => {
      notify.error(error);
    },
  });

  const { mutateAsync: update, isLoading: updating } = useMutation({
    mutationFn: updateAffiliateService,
    onSuccess: () => {
      notify.success('Afiliação atualizada com sucesso!');

      setScope(FormScopes.INDEX);
    },
    async onMutate({ id, info }) {
      await queryClient.cancelQueries({ queryKey: affiliatesQueryKey });
      const previousData = queryClient.getQueryData(affiliatesQueryKey);

      const newData = {
        ...previousData,
        results: previousData.results.map((previous) => {
          if (id.toString() === previous.id.toString()) {
            return {
              ...previous,
              ...info,
            };
          }

          return previous;
        }),
      };

      queryClient.setQueryData(affiliatesQueryKey, newData);

      return { previousData };
    },
    onError: (error) => {
      queryClient.invalidateQueries(affiliatesQueryKey);
      notify.error(error);
    },
  });

  const { mutateAsync: acceptAffiliate, isLoading: accepting } = useMutation({
    mutationFn: (ids) => acceptAffiliateService(ids),
    onSuccess: () => {
      queryClient.invalidateQueries('products');

      notify.success('Afiliação aceita com sucesso!');

      setScope(FormScopes.INDEX);

      table.setSelected([]);
    },
    onError: (error) => {
      notify.error(error);
    },
  });

  const { mutateAsync: rejectAffiliate, isLoading: rejecting } = useMutation({
    mutationFn: (ids) => rejectAffiliateService(ids),
    onSuccess: () => {
      queryClient.invalidateQueries('products');

      notify.success('Afiliação rejeitada com sucesso!');

      setScope(FormScopes.INDEX);

      table.setSelected([]);
    },
    onError: (error) => {
      notify.error(error);
    },
  });

  const { mutateAsync: deleteAffiliate, isLoading: deleting } = useMutation({
    mutationFn: deleteAffiliateService,
    onSuccess: () => {
      queryClient.invalidateQueries('products');

      notify.success('Afiliação cancelada com sucesso!');

      setScope(FormScopes.INDEX);
    },
    onError: (error) => {
      notify.error(error);
    },
  });

  const value = useMemo(
    () => ({
      table,
      affiliates: affiliates || [],
      fetching: isFetching,
      refreshAffiliates,
      affiliatesUpdatedAt,
      count,
      status,
      tabValue,
      setTabValue,
      products,
      fetchingProducts,
      ensureProducts,
      type,
      createAffiliate,
      creating,
      deleting,
      deleteAffiliate,
      rejecting,
      rejectAffiliate,
      accepting,
      acceptAffiliate,
      unblockAffiliate,
      blocking,
      unblocking,
      blockAffiliate,
      scope,
      setScope,
      form,
      update,
      loadingUpdate: updating,
    }),
    [
      table,
      affiliates,
      refreshAffiliates,
      affiliatesUpdatedAt,
      unblockAffiliate,
      blockAffiliate,
      isFetching,
      blocking,
      unblocking,
      count,
      status,
      tabValue,
      products,
      fetchingProducts,
      ensureProducts,
      type,
      createAffiliate,
      creating,
      deleting,
      deleteAffiliate,
      rejecting,
      rejectAffiliate,
      accepting,
      acceptAffiliate,
      scope,
      form,
      update,
      updating,
    ]
  );

  return <AffiliatesContext.Provider value={value}>{children}</AffiliatesContext.Provider>;
};

AffiliatesProvider.propTypes = {
  children: PropTypes.node.isRequired,
  type: PropTypes.string,
};

export default AffiliatesProvider;
